import moment from 'moment';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import DataTable from 'react-data-table-component';
import { useQuery } from 'react-query';
import Notification from 'components/Notification';
import { useNotif } from 'context/NotificationContext';
import logGamificationService from 'utils/network/log-gamification/log-gamification.service';
import LogPointGamificationDetail from 'components/LogPointGamificationDetail';
import { DEBOUNCE_TIME, PAGINATION } from 'utils/constants';

export default function LogPointGamification() {
  const columns = [
    {
      name: 'ID',
      selector: 'id',
      cell: function render(row) {
        return <>{row.id}</>;
      }
    },
    {
      name: 'Email',
      selector: 'email',
      cell: function render(row) {
        return <>{row?.customer?.email}</>;
      }
    },
    {
      name: 'Customer ID',
      selector: 'customer_id',
      cell: function render(row) {
        return <>{row?.customer?.id}</>;
      }
    },
    {
      name: 'Status',
      selector: 'injected',
      cell: function render(row) {
        return <>{row?.injected?.status}</>;
      }
    },
    {
      name: 'Total Played',
      selector: 'total_played',
      cell: function render(row) {
        return <>{row.total_played}</>;
      }
    },
    {
      name: 'Total Point',
      selector: 'total_point',
      cell: function render(row) {
        return <>{row.total_point}</>;
      }
    },
    {
      name: 'Claim At',
      selector: 'claim_at',
      cell: function render(row) {
        return (
          <span className="text-gray-400">
            {moment(row?.point?.claim_at).format('DD MMMM YYYY')}
          </span>
        );
      }
    }
  ];

  const { setNotification, notification } = useNotif();
  const [pagination, setPagination] = useState({
    page: PAGINATION.DEFAULT_PAGE,
    perPage: PAGINATION.DEFAULT_PER_PAGE,
    totalPage: PAGINATION.DEFAULT_TOTAL_PAGE,
    totalData: PAGINATION.DEFAULT_TOTAL_DATA
  });
  const [payload, setPayload] = useState({
    point_date_from: '',
    point_date_to: '',
    customer_id: 0,
    email: ''
  });
  const [sortProp, setSortProp] = useState({
    order: 'id',
    sort: 'desc'
  });

  const queryKey = useMemo(() => {
    return `log-point--${JSON.stringify({
      page: pagination.page,
      perPage: pagination.perPage,
      email: payload.email,
      customer_id: payload.customer_id,
      sortProp
    })}`;
  }, [
    pagination.page,
    pagination.perPage,
    payload.email,
    payload.customer_id,
    sortProp
  ]);

  // get list
  const { data: gamificationData, isLoading: getGamificationLoading } =
    useQuery({
      queryKey,
      queryFn: () => {
        return logGamificationService.list({
          limit: pagination.perPage,
          offset: pagination.page,
          order: sortProp.order,
          sort: sortProp.sort,
          email: payload.email,
          customer_id: payload.customer_id
        });
      }
    });

  function handlePerRowsChange(newPerPage, page) {
    setPagination({
      ...pagination,
      page,
      perPage: newPerPage,
      totalPage: Math.ceil(
        (gamificationData?.data?.total_data || 0) / newPerPage
      )
    });
  }

  function handlePageChange(page) {
    setPagination({
      ...pagination,
      page
    });
  }

  const handleSort = useCallback((column, sortDirection) => {
    setSortProp({
      order: column.selector,
      sort: sortDirection
    });
  });

  function closeNotification() {
    setNotification({ ...notification, show: false });
  }
  const searchDebounce = useRef();
  const searchCustomerIdDebounce = useRef();
  return (
    <div>
      <div className="flex flex-row items-center justify-between">
        <h2 className="my-6 text-xl font-semibold text-gray-700 dark:text-gray-200">
          Log Point Gamification
        </h2>
      </div>
      <div className="pb-3 grid grid-cols-1 md:grid-cols-1">
        <div className="flex flex-row items-center gap-3">
          <div className="relative">
            <input
              className="block w-full px-3 py-2 text-sm border appearance-none rounded-md dark:border-gray-600 dark:bg-gray-700 focus:border-purple-400 focus:outline-none focus:shadow-outline-purple dark:text-gray-300 dark:focus:shadow-outline-gray form-input"
              placeholder="Email"
              name="search"
              type="email"
              onChange={({ target: { value } }) => {
                if (searchDebounce.current) {
                  clearTimeout(searchDebounce.current);
                }

                // Regular expression for validating email format
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

                if (emailRegex.test(value)) {
                  searchDebounce.current = setTimeout(() => {
                    setPayload({
                      ...payload,
                      email: value
                    });
                  }, DEBOUNCE_TIME);
                } else {
                  // Clear the email in payload if the input is not a valid email address
                  setPayload({
                    ...payload,
                    email: ''
                  });
                }
              }}
            />
            <div className="absolute top-0 bottom-0 flex flex-row items-center h-full right-3">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
          </div>
          <div className="relative">
            <input
              className="block w-full px-3 py-2 text-sm border appearance-none rounded-md dark:border-gray-600 dark:bg-gray-700 focus:border-purple-400 focus:outline-none focus:shadow-outline-purple dark:text-gray-300 dark:focus:shadow-outline-gray form-input"
              placeholder="Customer ID"
              name="search"
              type="number"
              onChange={({ target: { value } }) => {
                if (searchCustomerIdDebounce.current) {
                  clearTimeout(searchCustomerIdDebounce.current);
                }

                searchCustomerIdDebounce.current = setTimeout(() => {
                  setPayload({
                    ...payload,
                    customer_id: parseInt(value)
                  });
                }, DEBOUNCE_TIME);
              }}
            />
            <div className="absolute top-0 bottom-0 flex flex-row items-center h-full right-3">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
          </div>
        </div>
        <div></div>
        <div></div>
        <div></div>
      </div>
      <DataTable
        columns={columns}
        data={gamificationData?.data?.data || []}
        sortServer
        onSort={handleSort}
        progressPending={getGamificationLoading}
        pagination
        paginationServer
        paginationTotalRows={gamificationData?.data?.total_data}
        onChangeRowsPerPage={handlePerRowsChange}
        onChangePage={handlePageChange}
        expandableRows
        expandableRowsComponent={LogPointGamificationDetail}
      />

      <Notification
        notification={notification}
        closeNotification={closeNotification}
      />
    </div>
  );
}
